import React, { useState, useEffect } from 'react';

/* Parse wedding date+time combined; if the computed moment has passed
   but the wedding day itself hasn't ended, roll forward to end-of-day
   so the countdown still ticks on the wedding day. */
export function parseWeddingDateTime(date, time) {
  if (!date) return null;
  const m = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d0 = new Date(date);
    return isNaN(d0.getTime()) ? null : d0;
  }
  const [hh = '16', mm = '00'] = (time && /^\d{1,2}:\d{2}/.test(time) ? time : '').split(':');
  let d = new Date(+m[1], +m[2] - 1, +m[3], +hh, +mm, 0, 0);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const endOfDay = new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59, 999);
  if (d <= now && endOfDay > now) return endOfDay;
  return d;
}

/**
 * Countdown — live countdown to a wedding date/time.
 *
 * Props:
 *   date           — 'YYYY-MM-DD' or ISO string
 *   time           — 'HH:mm' (optional)
 *   bc             — project.block_content (подписи берутся из bc.countdown.*Label)
 *   labels         — { d, h, m, s } (запасной вариант, если не передан bc)
 *   pad            — zero-pad numbers to N digits (default 2)
 *   classes        — { root, cell, num, label } — host template supplies styling
 */
export default function Countdown({
  date,
  time,
  bc,
  labels,
  pad = 2,
  classes = {},
}) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  /* Приоритет: пользовательские подписи из block_content.countdown →
     переданный labels (напр. кастом шаблона) → дефолт. Пусто не даём —
     старые сайты без этих полей показывают стандартный текст. */
  const c = (bc && bc.countdown) || {};
  const L = {
    d: c.dLabel || labels?.d || 'Дней',
    h: c.hLabel || labels?.h || 'Часов',
    m: c.mLabel || labels?.m || 'Минут',
    s: c.sLabel || labels?.s || 'Секунд',
  };

  useEffect(() => {
    const calc = () => {
      const target = parseWeddingDateTime(date, time);
      if (!target) { setT({ d: 0, h: 0, m: 0, s: 0 }); return; }
      const diff = Math.max(0, target - new Date());
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [date, time]);

  const cells = [
    { k: 'd', v: t.d, l: L.d },
    { k: 'h', v: t.h, l: L.h },
    { k: 'm', v: t.m, l: L.m },
    { k: 's', v: t.s, l: L.s },
  ];
  return (
    <div className={classes.root || 'countdown'} data-cb-section="countdown">
      {cells.map(c => (
        <div key={c.k} className={classes.cell || 'countdown-cell'}>
          <span className={classes.num   || 'countdown-num'}>{String(c.v).padStart(pad, '0')}</span>
          <span className={classes.label || 'countdown-label'}>{c.l}</span>
        </div>
      ))}
    </div>
  );
}
